// shared/api/sessions.js
// Sessions API - All session-related functions
import { supabase } from '../supabase';

/**
 * Create a new session
 * @param {string} userId 
 * @param {string} journeyId 
 * @param {string} currentStep - Default 'chat'
 * @returns {Promise<{session, error}>}
 */
export const createSession = async (userId, journeyId, currentStep = 'chat') => {
  try {
    const { data, error } = await supabase
      .from('daily_sessions')
      .insert({
        user_id: userId,
        journey_id: journeyId,
        current_step: currentStep,
        started_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Session created:', data.id);
    return { session: data, error: null };
  } catch (error) {
    console.error('Error creating session:', error);
    return { session: null, error };
  }
};

/**
 * Get session by ID with related data
 * @param {string} sessionId 
 * @returns {Promise<{session, error}>}
 */
export const getSessionById = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('daily_sessions')
      .select(`
        *,
        journeys (
          id,
          title,
          category,
          description
        ),
        chat_messages (
          id,
          role,
          content,
          created_at
        ),
        journal_entries (
          id,
          content,
          word_count,
          created_at
        )
      `)
      .eq('id', sessionId)
      .single();

    if (error) throw error;
    return { session: data, error: null };
  } catch (error) {
    console.error('Error fetching session:', error);
    return { session: null, error };
  }
};

/**
 * Update session step and data
 * @param {string} sessionId 
 * @param {Object} updates 
 * @returns {Promise<{session, error}>}
 */
export const updateSession = async (sessionId, updates) => {
  try {
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('daily_sessions')
      .update(updateData)
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return { session: data, error: null };
  } catch (error) {
    console.error('Error updating session:', error);
    return { session: null, error };
  }
};

/**
 * Complete a session
 * @param {string} sessionId 
 * @returns {Promise<{session, error}>}
 */
export const completeSession = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('daily_sessions')
      .update({
        completed_at: new Date().toISOString(),
        current_step: 'complete',
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Session completed:', sessionId);
    return { session: data, error: null };
  } catch (error) {
    console.error('Error completing session:', error);
    return { session: null, error };
  }
};

/**
 * Fetch user's sessions with filters
 * @param {string} userId 
 * @param {Object} options - {completed: boolean, limit: number}
 * @returns {Promise<{sessions, error}>}
 */
export const fetchUserSessions = async (userId, options = {}) => {
  try {
    let query = supabase
      .from('daily_sessions')
      .select(`
        id,
        created_at,
        started_at,
        completed_at,
        current_step,
        journey_id,
        meditation_completed,
        meditation_technique,
        suggested_technique,
        journeys (
          id,
          title,
          category
        ),
        chat_messages (
          id,
          role,
          content,
          created_at
        ),
        journal_entries (
          id,
          content,
          word_count
        )
      `)
      .eq('user_id', userId);

    // Apply filters
    if (options.completed !== undefined) {
      if (options.completed) {
        query = query.not('completed_at', 'is', null);
      } else {
        query = query.is('completed_at', null);
      }
    }

    // Apply ordering
    query = query.order('created_at', { ascending: false });

    // Apply limit
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { sessions: data || [], error: null };
  } catch (error) {
    console.error('Error fetching user sessions:', error);
    return { sessions: [], error };
  }
};

/**
 * Fetch completed sessions for progress tracking
 * @param {string} userId 
 * @returns {Promise<{sessions, error}>}
 */
export const fetchCompletedSessions = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('daily_sessions')
      .select(`
        id,
        created_at,
        completed_at,
        current_step,
        journey_id,
        journeys!inner (
          id,
          title,
          category
        )
      `)
      .eq('user_id', userId)
      .eq('current_step', 'complete')
      .order('completed_at', { ascending: false });

    if (error) throw error;
    return { sessions: data || [], error: null };
  } catch (error) {
    console.error('Error fetching completed sessions:', error);
    return { sessions: [], error };
  }
};

/**
 * Delete a session and all related data
 * @param {string} sessionId 
 * @returns {Promise<{success, error}>}
 */
export const deleteSession = async (sessionId) => {
  try {
    // Delete related data first
    await supabase.from('chat_messages').delete().eq('session_id', sessionId);
    await supabase.from('journal_entries').delete().eq('session_id', sessionId);
    
    // Delete session
    const { error } = await supabase
      .from('daily_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) throw error;
    console.log('✅ Session deleted:', sessionId);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting session:', error);
    return { success: false, error };
  }
};

/**
 * Delete multiple sessions
 * @param {Array<string>} sessionIds 
 * @returns {Promise<{success, error}>}
 */
export const deleteSessions = async (sessionIds) => {
  try {
    for (const sessionId of sessionIds) {
      await deleteSession(sessionId);
    }
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting sessions:', error);
    return { success: false, error };
  }
};

/**
 * Get active (incomplete) session for user
 * @param {string} userId 
 * @returns {Promise<{session, error}>}
 */
export const getActiveSession = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('daily_sessions')
      .select(`
        *,
        journeys (
          id,
          title,
          category
        )
      `)
      .eq('user_id', userId)
      .is('completed_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return { session: data, error: null };
  } catch (error) {
    console.error('Error fetching active session:', error);
    return { session: null, error };
  }
};

/**
 * Save suggested technique to session
 * @param {string} sessionId 
 * @param {string} techniqueId 
 * @param {Object} techniqueData - Optional additional data
 * @returns {Promise<{success, error}>}
 */
export const saveSuggestedTechnique = async (sessionId, techniqueId, techniqueData = {}) => {
  try {
    const { error } = await supabase
      .from('daily_sessions')
      .update({
        suggested_technique: techniqueId,
        technique_data: techniqueData,
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId);

    if (error) throw error;
    console.log('✅ Technique saved:', techniqueId);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error saving technique:', error);
    return { success: false, error };
  }
};
